import React, { useState, useRef, useEffect } from 'react';

/**
 * ByteChat
 * Template-prompt surface for the Byte coaching panel:
 *
 *   • Suggestion chips (canned starter prompts + optional action chips)
 *   • Local thread of {role: 'user' | 'assistant', text} for chip replies
 *
 * The parent passes `onAsk(prompt)` for conversational chips. If `onAsk`
 * returns a string (or a promise that resolves to one), it's appended as
 * the assistant reply; otherwise we fall back to a friendly placeholder.
 */
const DEFAULT_PLACEHOLDER_REPLY =
  "I'm only doing one-shot insights for now. Full chat is coming soon. " +
  "In the meantime, regenerate the insights or ask in the snippet's notes.";

const ByteChat = ({
  chips = [],
  onAsk,
  disabled = false
}) => {
  const [messages, setMessages] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, isPending]);

  const submit = async (text) => {
    const prompt = (text || '').trim();
    if (!prompt || isPending || disabled) return;

    setMessages((prev) => [...prev, { role: 'user', text: prompt }]);
    setIsPending(true);

    let reply = DEFAULT_PLACEHOLDER_REPLY;
    try {
      if (typeof onAsk === 'function') {
        const result = await onAsk(prompt);
        if (typeof result === 'string' && result.trim()) {
          reply = result.trim();
        }
      }
    } catch (err) {
      reply = "Byte hit a snag answering that. Mind trying again?";
    }

    setMessages((prev) => [...prev, { role: 'assistant', text: reply }]);
    setIsPending(false);
  };

  return (
    <div className="byte-companion__chat">
      {messages.length > 0 && (
        <div
          ref={threadRef}
          className="byte-companion__chat-thread"
          aria-live="polite"
        >
          {messages.map((m, i) => (
            <div
              key={i}
              className={[
                'byte-companion__chat-message',
                `byte-companion__chat-message--${m.role}`
              ].join(' ')}
            >
              {m.text}
            </div>
          ))}
          {isPending && (
            <div className="byte-companion__chat-message byte-companion__chat-message--assistant byte-companion__chat-message--pending">
              <span className="byte-companion__chat-typing" aria-hidden>
                <span /><span /><span />
              </span>
              <span className="sr-only">Byte is thinking…</span>
            </div>
          )}
        </div>
      )}

      {chips.length > 0 && (
        <div className="byte-companion__chips">
          {chips.map((chip, i) => {
            // Chips can be plain strings (submitted as a chat prompt
            // through `onAsk`) or `{ label, action, icon, variant }`
            // descriptors that fire their own handler — useful for
            // surfacing primary side actions like "Generate insights"
            // alongside conversational starters.
            const isAction = chip && typeof chip === 'object';
            const label = isAction ? chip.label : chip;
            const variant = isAction ? chip.variant : null;
            const Icon = isAction ? chip.icon : null;
            const handleClick = isAction
              ? () => {
                if (typeof chip.action === 'function') chip.action();
              }
              : () => submit(chip);
            return (
              <button
                key={`${label}-${i}`}
                type="button"
                className={[
                  'byte-companion__chip',
                  variant === 'primary' && 'byte-companion__chip--primary',
                  isAction && 'byte-companion__chip--action'
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={handleClick}
                disabled={disabled || isPending || (isAction && chip.disabled)}
              >
                {Icon && (
                  <Icon
                    className="byte-companion__chip-icon"
                    aria-hidden="true"
                  />
                )}
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ByteChat;
